import { useState, useMemo, useEffect } from 'react'
import { useGetCenterApplicationsQuery } from '../../redux/services/centerApplicationService'
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline'
import AdminPageLayout from '../../components/admin/AdminPageLayout'
import ApplicationTableRow from '../../components/admin/ApplicationTableRow'
import AdminLoadingState from '../../components/admin/AdminLoadingState'
import AdminErrorState from '../../components/admin/AdminErrorState'
import BackToDashboardLink from '../../components/admin/BackToDashboardLink'

type SortField = 'center_name' | 'second_approval_at' | 'center_type' | 'state'
type SortDirection = 'asc' | 'desc'

export default function ApprovedApplications() {
  const {
    data: applications = [],
    isLoading,
    error
  } = useGetCenterApplicationsQuery({ status: 'accepted' })

  const [searchTerm, setSearchTerm] = useState('')
  const [filterCenterType, setFilterCenterType] = useState<string>('all')
  const [filterState, setFilterState] = useState<string>('all')
  const [filterAssociate, setFilterAssociate] = useState<string>('all')
  const [sortField, setSortField] = useState<SortField>('second_approval_at')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')
  const [showFilters, setShowFilters] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)

  const states = useMemo(() => {
    const stateSet = new Set(applications.map((app) => app.state))
    return Array.from(stateSet).sort()
  }, [applications])

  const filteredAndSortedApplications = useMemo(() => {
    const filtered = applications.filter((app) => {
      const matchesSearch =
        searchTerm === '' ||
        app.center_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.city.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.state.toLowerCase().includes(searchTerm.toLowerCase())

      const matchesCenterType =
        filterCenterType === 'all' || app.center_type === filterCenterType

      const matchesState = filterState === 'all' || app.state === filterState

      const matchesAssociate =
        filterAssociate === 'all' ||
        (filterAssociate === 'associate' && app.is_associate) ||
        (filterAssociate === 'full' && !app.is_associate)

      return (
        matchesSearch && matchesCenterType && matchesState && matchesAssociate
      )
    })

    filtered.sort((a, b) => {
      let aValue: string | number = a[sortField] || ''
      let bValue: string | number = b[sortField] || ''

      if (sortField === 'second_approval_at') {
        aValue = new Date(aValue).getTime()
        bValue = new Date(bValue).getTime()
      } else if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase()
        bValue = bValue.toLowerCase()
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1
      return 0
    })

    return filtered
  }, [
    applications,
    searchTerm,
    filterCenterType,
    filterState,
    filterAssociate,
    sortField,
    sortDirection
  ])

  // Pagination calculations
  const totalPages = Math.ceil(
    filteredAndSortedApplications.length / itemsPerPage
  )
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const paginatedApplications = filteredAndSortedApplications.slice(
    startIndex,
    endIndex
  )

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm, filterCenterType, filterState, filterAssociate])

  // Handle items per page change
  const handleItemsPerPageChange = (value: string) => {
    setItemsPerPage(Number(value))
    setCurrentPage(1)
  }

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return <div className="w-4 h-4" />
    }
    return sortDirection === 'asc' ? (
      <ChevronUpIcon className="w-4 h-4" />
    ) : (
      <ChevronDownIcon className="w-4 h-4" />
    )
  }

  if (isLoading) {
    return (
      <AdminPageLayout>
        <AdminLoadingState type="table" />
      </AdminPageLayout>
    )
  }

  if (error) {
    return (
      <AdminPageLayout>
        <h1 className="text-2xl font-medium text-[#A6410A] mb-8">
          Approved Applications
        </h1>
        <AdminErrorState message="Error loading applications" />
      </AdminPageLayout>
    )
  }

  return (
    <AdminPageLayout>
      <div className="mb-8">
        <h1 className="text-2xl font-medium text-[#A6410A] mb-4">
          Approved Applications
        </h1>
        <p className="text-gray-600">
          {filteredAndSortedApplications.length} of {applications.length}{' '}
          applications shown
        </p>
      </div>

      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-4 border-b border-gray-200">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by center name, city, or state..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#142841] focus:border-transparent"
                />
              </div>
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <FunnelIcon className="w-5 h-5" />
              Filters
              {(filterCenterType !== 'all' ||
                filterState !== 'all' ||
                filterAssociate !== 'all') && (
                <span className="bg-[#A6410A] text-white text-xs px-2 py-1 rounded-full">
                  {
                    [
                      filterCenterType !== 'all',
                      filterState !== 'all',
                      filterAssociate !== 'all'
                    ].filter(Boolean).length
                  }
                </span>
              )}
            </button>
          </div>

          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200 flex flex-wrap gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Center Type
                </label>
                <select
                  value={filterCenterType}
                  onChange={(e) => setFilterCenterType(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#142841] focus:border-transparent"
                >
                  <option value="all">All Types</option>
                  <option value="pediatric">Pediatric</option>
                  <option value="adult">Adult</option>
                  <option value="whole_life">Whole Life</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  State
                </label>
                <select
                  value={filterState}
                  onChange={(e) => setFilterState(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#142841] focus:border-transparent"
                >
                  <option value="all">All States</option>
                  {states.map((state) => (
                    <option key={state} value={state}>
                      {state}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Member Type
                </label>
                <select
                  value={filterAssociate}
                  onChange={(e) => setFilterAssociate(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#142841] focus:border-transparent"
                >
                  <option value="all">All Members</option>
                  <option value="full">Full Members</option>
                  <option value="associate">Associate Members</option>
                </select>
              </div>
              {(filterCenterType !== 'all' ||
                filterState !== 'all' ||
                filterAssociate !== 'all') && (
                <button
                  onClick={() => {
                    setFilterCenterType('all')
                    setFilterState('all')
                    setFilterAssociate('all')
                  }}
                  className="self-end px-4 py-2 text-sm text-[#A6410A] hover:text-[#894d35] font-medium"
                >
                  Clear Filters
                </button>
              )}
            </div>
          )}
        </div>

        {filteredAndSortedApplications.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-gray-500 text-lg">
              {searchTerm ||
              filterCenterType !== 'all' ||
              filterState !== 'all' ||
              filterAssociate !== 'all'
                ? 'No applications match your search criteria.'
                : 'No approved applications at this time.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left p-4">
                    <button
                      onClick={() => handleSort('center_name')}
                      className="flex items-center gap-1 font-medium text-sm text-gray-700 hover:text-[#142841]"
                    >
                      Center Name
                      <SortIcon field="center_name" />
                    </button>
                  </th>
                  <th className="text-left p-4">
                    <button
                      onClick={() => handleSort('center_type')}
                      className="flex items-center gap-1 font-medium text-sm text-gray-700 hover:text-[#142841]"
                    >
                      Type
                      <SortIcon field="center_type" />
                    </button>
                  </th>
                  <th className="text-left p-4">
                    <button
                      onClick={() => handleSort('state')}
                      className="flex items-center gap-1 font-medium text-sm text-gray-700 hover:text-[#142841]"
                    >
                      Location
                      <SortIcon field="state" />
                    </button>
                  </th>
                  <th className="text-left p-4">
                    <span className="font-medium text-sm text-gray-700">
                      Patients
                    </span>
                  </th>
                  <th className="text-left p-4">
                    <button
                      onClick={() => handleSort('second_approval_at')}
                      className="flex items-center gap-1 font-medium text-sm text-gray-700 hover:text-[#142841]"
                    >
                      Approved Date
                      <SortIcon field="second_approval_at" />
                    </button>
                  </th>
                  <th className="text-left p-4">
                    <span className="font-medium text-sm text-gray-700">
                      Member Type
                    </span>
                  </th>
                  <th className="text-center p-4">
                    <span className="font-medium text-sm text-gray-700">
                      Actions
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedApplications.map((application) => (
                  <ApplicationTableRow
                    key={application.id}
                    application={application}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination Controls */}
      {filteredAndSortedApplications.length > 0 && (
        <div className="bg-white rounded-lg shadow p-4 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="text-sm text-gray-700">
              Showing {startIndex + 1} to{' '}
              {Math.min(endIndex, filteredAndSortedApplications.length)} of{' '}
              {filteredAndSortedApplications.length} results
            </div>
            <div className="flex items-center gap-2">
              <label htmlFor="items-per-page" className="text-sm text-gray-700">
                Show:
              </label>
              <select
                id="items-per-page"
                value={itemsPerPage}
                onChange={(e) => handleItemsPerPageChange(e.target.value)}
                className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#142841] focus:border-transparent"
              >
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
            </div>
          </div>
          {totalPages > 1 && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                aria-label="Previous page"
              >
                <ChevronLeftIcon className="w-5 h-5" />
              </button>

              <div className="flex items-center gap-1">
                {/* Show first page */}
                {currentPage > 2 && (
                  <>
                    <button
                      onClick={() => setCurrentPage(1)}
                      className="px-3 py-1 text-sm rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      1
                    </button>
                    {currentPage > 3 && (
                      <span className="px-1 text-gray-500">...</span>
                    )}
                  </>
                )}

                {/* Show previous page */}
                {currentPage > 1 && (
                  <button
                    onClick={() => setCurrentPage(currentPage - 1)}
                    className="px-3 py-1 text-sm rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    {currentPage - 1}
                  </button>
                )}

                {/* Show current page */}
                <button className="px-3 py-1 text-sm rounded-lg bg-[#142841] text-white">
                  {currentPage}
                </button>

                {/* Show next page */}
                {currentPage < totalPages && (
                  <button
                    onClick={() => setCurrentPage(currentPage + 1)}
                    className="px-3 py-1 text-sm rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    {currentPage + 1}
                  </button>
                )}

                {/* Show last page */}
                {currentPage < totalPages - 1 && (
                  <>
                    {currentPage < totalPages - 2 && (
                      <span className="px-1 text-gray-500">...</span>
                    )}
                    <button
                      onClick={() => setCurrentPage(totalPages)}
                      className="px-3 py-1 text-sm rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      {totalPages}
                    </button>
                  </>
                )}
              </div>

              <button
                onClick={() =>
                  setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                }
                disabled={currentPage === totalPages}
                className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                aria-label="Next page"
              >
                <ChevronRightIcon className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>
      )}

      <div className="flex justify-between items-center">
        <BackToDashboardLink />
      </div>
    </AdminPageLayout>
  )
}
